import { createAction, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { BasicGasCalculation, Equipment, GasCalculationData, GasCalculationOutPut, PumpCapacityCurveGraphData, ChargePumpData, FinalCalculatedData, SpecialPumpCalculationOutput } from "../../model/calculations/calculation";
import { convertObjToDropdown, DropdownOptions } from "../../../utilities/commonUtilities";

export interface State {
    loading: boolean;
    error: string | null;
    BasicGasCalculation: BasicGasCalculation | null;
    GasFinalCalculatedData: FinalCalculatedData | null;
    GasCalculationComplete: boolean;
    hPRating: string | number;
    GasCalculationOutPut: GasCalculationOutPut | null;
    GasIntakeOutput: GasCalculationData | null;
    GasAnchorOutput: GasCalculationData | null;
    chargePumpCapacityCurveData: PumpCapacityCurveGraphData | null;
    chargePump2CapacityCurveData: PumpCapacityCurveGraphData | null;
    chargePump3CapacityCurveData: PumpCapacityCurveGraphData | null;
    showGasSeparatorsEquipment: boolean;
    showGasHandlerEquipments: boolean;
    showSRGasPump1Equipments: boolean;
    showSRGasPump2Equipments: boolean;
    showSRGasPump3Equipments: boolean;
    GasSeparatorsEquipmentDropdown: any;
    separatorList: DropdownOptions[];
    sepName: string;
    anchorTubingODList: DropdownOptions[];
    anchorODList: [];
    TFVWarning: boolean;
    existedGasSepEquipments: any;
    existedGasHandlerEquipments: any;
    existedSRGasPump1Equipments: any;
    existedSRGasPump2Equipments: any;
    existedSRGasPump3Equipments: any;
    gasChargePump1Data: ChargePumpData | null;
    gasChargePump2Data: ChargePumpData | null;
    gasChargePump3Data: ChargePumpData | null;
    GKXCalculationData: SpecialPumpCalculationOutput | null;
    gkxPumpCapacityCurveData: any;
    CP1GraphCurveData: any;
    CP2GraphCurveData: any;
    CP3GraphCurveData: any;
    gasSep1SeparatorEff: string;
    gasSep2SeparatorEff: string;
    gasSep3SeparatorEff: string;
    gasSep4SeparatorEff: string;
    chargePump1EquipmentsList: Equipment[] | any[];
    chargePump2EquipmentsList: Equipment[] | any[];
    chargePump3EquipmentsList: Equipment[] | any[];
    chargePumpEquipments: any[];
    CP1CalculationData: any;
    CP2CalculationData: any;
    CP3CalculationData: any;
    chargePumpName: string;
    chargePump2Name: string;
    chargePump3Name: string;
    Notification: {
        show: boolean,
        message: string,
        type: string
    }
};

export const initialState: State = {
    loading: false,
    error: null,
    BasicGasCalculation: null,
    GasFinalCalculatedData: null,
    GasCalculationComplete: false,
    hPRating: 0,
    GasCalculationOutPut: null,
    GasIntakeOutput: null,
    GasAnchorOutput: null,
    chargePumpCapacityCurveData: null,
    chargePump2CapacityCurveData: null,
    chargePump3CapacityCurveData: null,
    showGasSeparatorsEquipment: false,
    showGasHandlerEquipments: false,
    showSRGasPump1Equipments: false,
    showSRGasPump2Equipments: false,
    showSRGasPump3Equipments: false,
    GasSeparatorsEquipmentDropdown: [],
    separatorList: [],
    sepName: "",
    anchorTubingODList: [],
    anchorODList: [],
    TFVWarning: false,
    existedGasSepEquipments: [],
    existedGasHandlerEquipments: [],
    existedSRGasPump1Equipments: [],
    existedSRGasPump2Equipments: [],
    existedSRGasPump3Equipments: [],
    gasChargePump1Data: null,
    gasChargePump2Data: null,
    gasChargePump3Data: null,
    gkxPumpCapacityCurveData: null,
    chargePump1EquipmentsList: [],
    chargePump2EquipmentsList: [],
    chargePump3EquipmentsList: [],
    chargePumpEquipments: [],
    GKXCalculationData: null,
    CP1CalculationData: null,
    CP2CalculationData: null,
    CP3CalculationData: null,
    CP1GraphCurveData: null,
    CP2GraphCurveData: null,
    CP3GraphCurveData: null,
    gasSep1SeparatorEff: "",
    gasSep2SeparatorEff: "",
    gasSep3SeparatorEff: "",
    gasSep4SeparatorEff: "",
    chargePumpName: "",
    chargePump2Name: "",
    chargePump3Name: "",
    Notification: {
        show: false,
        message: "",
        type: ""
    }
};

export const gasCalculationStart = createAction("gasCalculation/gasCalculationStart");
export const fetchGasAnchorCalculation = createAction("gasCalculation/fetchGasAnchorCalculation");
export const fetchGasSP1Calculation = createAction("gasCalculation/fetchGasSP1Calculation");
export const fetchGasSP2Calculation = createAction("gasCalculation/fetchGasSP2Calculation");
export const fetchGasSP3Calculation = createAction("gasCalculation/fetchGasSP3Calculation");
export const fetchGasSP4Calculation = createAction("gasCalculation/fetchGasSP4Calculation");
export const fetchGasGKXCalculation = createAction("gasCalculation/fetchGasGKXCalculation");
export const fetchGasCP1Calculation = createAction("gasCalculation/fetchGasCP1Calculation");
export const fetchGasCP2Calculation = createAction("gasCalculation/fetchGasCP2Calculation");
export const fetchGasCP3Calculation = createAction("gasCalculation/fetchGasCP3Calculation");
export const fetchFinalCalculationData = createAction("gasCalculation/fetchFinalCalculationData");
export const fetchGasSeparatorEquipments = createAction("gasCalculation/fetchGasSeparatorEquipments");
export const fetchEquipmentsGasHandler = createAction("gasCalculation/fetchEquipmentsGasHandler");
export const fetchEquipmentsChargePumpOne = createAction("gasCalculation/fetchEquipmentsChargePump");
export const fetchEquipmentsChargePumpTwo = createAction("gasCalculation/fetchEquipmentsChargePump2");
export const fetchEquipmentsChargePumpThree = createAction("gasCalculation/fetchEquipmentsChargePump3");

const gasCalculationSlice = createSlice({
    name: "gasCalculation",
    initialState,
    reducers: {
        resetGascalculations: () => ({
            ...initialState,
        }),
        setGasCalculation: (
            state,
            action: PayloadAction<BasicGasCalculation>
        ) => {
            state.BasicGasCalculation = action.payload;
        },
        setGasCalculation2: (
            state,
            action: PayloadAction<BasicGasCalculation>
        ) => {
            state.BasicGasCalculation = action.payload;
        },
        setGasCalculationOutput: (state, action: PayloadAction<GasCalculationOutPut>) => {
            state.GasCalculationOutPut = {
                ...state?.GasCalculationOutPut,
                ...action.payload
            }
        },
        setGasDesign: (state, action: PayloadAction<string>) => {
            if (state.BasicGasCalculation) {
                state.BasicGasCalculation = {
                    ...state?.BasicGasCalculation,
                    gasDesign: action.payload,
                };
            }
        },
        setGasSeparatorList: (state, action: PayloadAction<any>) => {
            if (state.BasicGasCalculation) {
                state.BasicGasCalculation = {
                    ...state?.BasicGasCalculation,
                    separatorList: action?.payload && convertObjToDropdown(action?.payload) || [],
                    sepSelcted: state?.BasicGasCalculation?.sepSelcted ?? "",
                };
                const sepName = state?.BasicGasCalculation?.separatorList?.find(x => x?.id === state?.BasicGasCalculation?.sepSelcted);
                state.BasicGasCalculation.sepSelcted = sepName === undefined ? "" : state?.BasicGasCalculation?.sepSelcted;
            }
        },
        setSelectedGasSeparator: (state, action: PayloadAction<string>) => {
            if (state.BasicGasCalculation) {
                state.BasicGasCalculation = {
                    ...state?.BasicGasCalculation,
                    sepSelcted: action.payload,
                };
            }
        },
        setSepName: (state, action) => {
            state.sepName = action?.payload;
        },
        setNoOfSeparators: (state, action: PayloadAction<string>) => {
            if (state.BasicGasCalculation) {
                state.BasicGasCalculation = {
                    ...state?.BasicGasCalculation,
                    noOfGasSeps: action.payload,
                };
            }
        },
        setGasAnchorOD: (state, action: PayloadAction<string>) => {
            if (state.BasicGasCalculation) {
                state.BasicGasCalculation = {
                    ...state?.BasicGasCalculation,
                    anchorOD: action.payload,
                };
            }
        },
        setGasTubingOD: (state, action: PayloadAction<string>) => {
            if (state.BasicGasCalculation) {
                state.BasicGasCalculation = {
                    ...state?.BasicGasCalculation,
                    tubingOD: action.payload,
                };
            }
        },
        setGasAnchorID: (state, action: PayloadAction<string>) => {
            if (state.BasicGasCalculation) {
                state.BasicGasCalculation = {
                    ...state?.BasicGasCalculation,
                    anchorID: action.payload,
                };
            }
        },
        setGasTubingODList: (state, action: PayloadAction<any>) => {
            const updatedTubingODList = action?.payload ? Object.entries(action?.payload).map(([name, id]) => ({
                id: String(id),
                name,
            })) : [];
            state.anchorTubingODList = updatedTubingODList;
        },
        setAnchorODList: (state, action: PayloadAction<any>) => {
            state.anchorODList = action.payload;
        },
        setChargePumpList: (state, action: PayloadAction<any>) => {
            if (state.BasicGasCalculation) {
                return ({
                    ...state,
                    BasicGasCalculation: {
                        ...state?.BasicGasCalculation,
                        chargePumpList: action.payload,
                        chargePump: state?.BasicGasCalculation.chargePump,
                    }
                });
            }
        },
        setChargePumpSelected: (state, action: PayloadAction<string>) => {
            if (state.BasicGasCalculation) {
                state.BasicGasCalculation = {
                    ...state?.BasicGasCalculation,
                    chargePump: action.payload
                };
            }
            state.CP1GraphCurveData = action.payload !== "" ? state?.CP1GraphCurveData : null;
        },
        setChargePumpList2: (state, action: PayloadAction<any>) => {
            if (state.BasicGasCalculation) {
                return ({
                    ...state,
                    BasicGasCalculation: {
                        ...state?.BasicGasCalculation,
                        chargePumpList2: action.payload,
                        chargePump2: state?.BasicGasCalculation?.chargePump2 ?? ''
                    }
                });
            }
        },
        setChargePump2Selected: (state, action: PayloadAction<string>) => {
            if (state.BasicGasCalculation) {
                state.BasicGasCalculation = {
                    ...state?.BasicGasCalculation,
                    chargePump2: action.payload
                };
            }
            state.CP2GraphCurveData = action?.payload !== "" ? state?.CP2GraphCurveData : null;
        },
        setChargePumpList3: (state, action: PayloadAction<any>) => {
            if (state.BasicGasCalculation) {
                return ({
                    ...state,
                    BasicGasCalculation: {
                        ...state?.BasicGasCalculation,
                        chargePumpList3: action.payload,
                        chargePump3: state?.BasicGasCalculation?.chargePump3 ?? ''
                    }
                });
            }
        },
        setChargePump3Selected: (state, action: PayloadAction<string>) => {
            if (state.BasicGasCalculation) {
                state.BasicGasCalculation = {
                    ...state?.BasicGasCalculation,
                    chargePump3: action.payload
                };
            }
            state.CP3GraphCurveData = action?.payload !== "" ? state?.CP3GraphCurveData : null;
        },
        setUseGasHandler: (state, action: PayloadAction<string>) => {
            if (state.BasicGasCalculation) {
                state.BasicGasCalculation = {
                    ...state?.BasicGasCalculation,
                    useOfChgPmp: action.payload,
                };
            }
        },
        setGasHandler: (state, action: PayloadAction<string>) => {
            if (state.BasicGasCalculation) {
                state.BasicGasCalculation = {
                    ...state?.BasicGasCalculation,
                    chargePumpGKX: action.payload,
                };
                state.BasicGasCalculation.chargePumpGKX = action.payload;
                state.gkxPumpCapacityCurveData = action?.payload !== "" ? state?.gkxPumpCapacityCurveData : null;
            }
        },
        setTypeOfShaft: (state, action: PayloadAction<number | string>) => {
            if (state.BasicGasCalculation) {
                state.BasicGasCalculation = {
                    ...state?.BasicGasCalculation,
                    hdn_GKX_ShaftType: action.payload
                };
            }
        },
        setGasPowerRating: (state, action: PayloadAction<string | number>) => {
            state.hPRating = action.payload;
        },
        setUseChargePump: (state, action: PayloadAction<string>) => {
            if (state.BasicGasCalculation) {
                state.BasicGasCalculation = {
                    ...state?.BasicGasCalculation,
                    useOfChgPmpNew: action.payload,
                };
            }
        },
        setTypeOfShaft1: (state, action: PayloadAction<number>) => {
            if (state.BasicGasCalculation) {
                state.BasicGasCalculation = {
                    ...state?.BasicGasCalculation,
                    hdn_ChargePump1_ShaftType: action.payload,
                };
            }
        },
        setTypeOfShaft2: (state, action: PayloadAction<number>) => {
            if (state.BasicGasCalculation) {
                state.BasicGasCalculation = {
                    ...state?.BasicGasCalculation,
                    hdn_ChargePump2_ShaftType: action.payload,
                };
            }
        },
        setTypeOfShaft3: (state, action: PayloadAction<number>) => {
            if (state.BasicGasCalculation) {
                state.BasicGasCalculation = {
                    ...state?.BasicGasCalculation,
                    hdn_ChargePump3_ShaftType: action.payload,
                };
            }
        },
        setDesiredNoOfStages1: (state, action: PayloadAction<string | number>) => {
            if (state.BasicGasCalculation) {
                state.BasicGasCalculation = {
                    ...state?.BasicGasCalculation,
                    chargePumpDesrdStge: action.payload,
                };
            }
            state.CP1GraphCurveData = Number(action?.payload) > 0 ? state?.CP1GraphCurveData : null;
        },
        setDesiredNoOfStages2: (state, action: PayloadAction<string | number>) => {
            if (state.BasicGasCalculation) {
                state.BasicGasCalculation = {
                    ...state?.BasicGasCalculation,
                    chargePumpDesrdStge2: action.payload,
                };
            }
            state.CP2GraphCurveData = Number(action?.payload) > 0 ? state?.CP2GraphCurveData : null;
        },
        setDesiredNoOfStages3: (state, action: PayloadAction<string | number>) => {
            if (state.BasicGasCalculation) {
                state.BasicGasCalculation = {
                    ...state?.BasicGasCalculation,
                    chargePumpDesrdStge3: action.payload,
                };
            }
            state.CP3GraphCurveData = Number(action?.payload) > 0 ? state?.CP2GraphCurveData : null;
        },
        setGasIntakeOutput: (state, action: PayloadAction<any>) => {
            state.GasCalculationOutPut = {
                ...state?.GasCalculationOutPut,
                Intake_Bg: action?.payload?.Intake_Bg ?? state?.GasCalculationOutPut?.Intake_Bg,
                Intake_Bo: action?.payload?.Intake_Bo ?? state?.GasCalculationOutPut?.Intake_Bo,
                Intake_FinalComposite_SpGr: action?.payload?.Intake_FinalComposite_SpGr ?? state?.GasCalculationOutPut?.Intake_FinalComposite_SpGr,
                Intake_FinalFLOP: action?.payload?.Intake_FinalFLOP ?? state?.GasCalculationOutPut?.Intake_FinalFLOP,
                Intake_FinalTemperature: action?.payload?.Intake_FinalTemperature ?? state?.GasCalculationOutPut?.Intake_FinalTemperature,
                Intake_FreeGas: action?.payload?.Intake_FreeGas ?? state?.GasCalculationOutPut?.Intake_FreeGas,
                Intake_GasCompressibility: action?.payload?.Intake_GasCompressibility ?? state?.GasCalculationOutPut?.Intake_GasCompressibility,
                Intake_GLR: action?.payload?.Intake_GLR ?? state?.GasCalculationOutPut?.Intake_GLR,
                Intake_GOR: action?.payload?.Intake_GOR ?? state?.GasCalculationOutPut?.Intake_GOR,
                Intake_GVF: action?.payload?.Intake_GVF ?? state?.GasCalculationOutPut?.Intake_GVF,
                Intake_LiquidPhaseDensity: action?.payload?.Intake_LiquidPhaseDensity ?? state?.GasCalculationOutPut?.Intake_LiquidPhaseDensity,
                Intake_NaturalSeperatorEff: action?.payload?.Intake_NaturalSeperatorEff ?? state?.GasCalculationOutPut?.Intake_NaturalSeperatorEff,
                Intake_Pressure: action?.payload?.Intake_Pressure ?? state?.GasCalculationOutPut?.Intake_Pressure,
                Intake_Rs: action?.payload?.Intake_Rs ?? state?.GasCalculationOutPut?.Intake_Rs,
                Intake_SeperatorEff: action?.payload?.Intake_SeperatorEff ?? state?.GasCalculationOutPut?.Intake_SeperatorEff,
                Intake_TotalFluidVolume: action?.payload?.Intake_TotalFluidVolume ?? state?.GasCalculationOutPut?.Intake_TotalFluidVolume,
                Intake_VolumeOfGas: action?.payload?.Intake_VolumeOfGas ?? state?.GasCalculationOutPut?.Intake_VolumeOfGas,
                Intake_VolumeOfOil: action?.payload?.Intake_VolumeOfOil ?? state?.GasCalculationOutPut?.Intake_VolumeOfOil,
                Intake_VolumeOfWater: action?.payload?.Intake_VolumeOfWater ?? state?.GasCalculationOutPut?.Intake_VolumeOfWater,
                Intake_LiquidPhaseSpGr: action?.payload?.Intake_LiquidPhaseSpGr ?? state?.GasCalculationOutPut?.Intake_LiquidPhaseSpGr
            }
        },
        setGasAnchorOutput: (state, action: PayloadAction<any>) => {
            if (state.GasCalculationOutPut) {
                state.GasCalculationOutPut = {
                    ...state.GasCalculationOutPut,
                    Anchor_Bg: action?.payload?.Anchor_Bg ?? state.GasCalculationOutPut.Anchor_Bg,
                    Anchor_Bo: action?.payload?.Anchor_Bo ?? state.GasCalculationOutPut.Anchor_Bo,
                    Anchor_FinalComposite_SpGr: action?.payload?.Anchor_FinalComposite_SpGr ?? state.GasCalculationOutPut.Anchor_FinalComposite_SpGr,
                    Anchor_FinalFLOP: action?.payload?.Anchor_FinalFLOP ?? state.GasCalculationOutPut.Anchor_FinalFLOP,
                    Anchor_FinalTemperature: action?.payload?.Anchor_FinalTemperature ?? state.GasCalculationOutPut.Anchor_FinalTemperature,
                    Anchor_FreeGas: action?.payload?.Anchor_FreeGas ?? state.GasCalculationOutPut.Anchor_FreeGas,
                    Anchor_GasCompressibility: action?.payload?.Anchor_GasCompressibility ?? state.GasCalculationOutPut.Anchor_GasCompressibility,
                    Anchor_GLR: action?.payload?.Anchor_GLR ?? state.GasCalculationOutPut.Anchor_GLR,
                    Anchor_GOR: action?.payload?.Anchor_GOR ?? state.GasCalculationOutPut.Anchor_GOR,
                    Anchor_GVF: action?.payload?.Anchor_GVF ?? state.GasCalculationOutPut.Anchor_GVF,
                    Anchor_LiquidPhaseDensity: action?.payload?.Anchor_LiquidPhaseDensity ?? state.GasCalculationOutPut.Anchor_LiquidPhaseDensity,
                    Anchor_NaturalSeperatorEff: action?.payload?.Anchor_NaturalSeperatorEff ?? state.GasCalculationOutPut.Anchor_NaturalSeperatorEff,
                    Anchor_Pressure: action?.payload?.Anchor_Pressure ?? state.GasCalculationOutPut.Anchor_Pressure,
                    Anchor_Rs: action?.payload?.Anchor_Rs ?? state.GasCalculationOutPut.Anchor_Rs,
                    Anchor_SeperatorEff: action?.payload?.Anchor_SeperatorEff ?? state.GasCalculationOutPut.Anchor_SeperatorEff,
                    Anchor_TotalFluidVolume: action?.payload?.Anchor_TotalFluidVolume ?? state.GasCalculationOutPut.Anchor_TotalFluidVolume,
                    Anchor_VolumeOfGas: action?.payload?.Anchor_VolumeOfGas ?? state.GasCalculationOutPut.Anchor_VolumeOfGas,
                    Anchor_VolumeOfOil: action?.payload?.Anchor_VolumeOfOil ?? state.GasCalculationOutPut.Anchor_VolumeOfOil,
                    Anchor_VolumeOfWater: action?.payload?.Anchor_VolumeOfWater ?? state.GasCalculationOutPut.Anchor_VolumeOfWater,
                    Anchor_LiquidPhaseSpGr: action?.payload?.Anchor_LiquidPhaseSpGr ?? state.GasCalculationOutPut.Anchor_LiquidPhaseSpGr
                }
            }
        },
        setGasSeparatorsEquipmentDropdown: (state, action) => {
            if (state?.BasicGasCalculation) {
                state.BasicGasCalculation = {
                    ...state.BasicGasCalculation,
                    EquipmentListForGasSeparator: action?.payload?.length > 0 ? action?.payload.map((equipment: any) => ({
                        ...equipment,
                        label: equipment.pN_Description,
                        value: equipment?.id,
                        diameter: equipment?.od?.toString(),
                    })) : [],
                }
            }
        },
        setExistedGasSepEquipments: (state, action) => {
            state.existedGasSepEquipments = action?.payload;
        },
        setExistedGasHandlerEquipments: (state, action) => {
            state.existedGasHandlerEquipments = action.payload;
        },
        setExistedSRGasPump1Equipments: (state, action) => {
            state.existedSRGasPump1Equipments = action.payload;
        },
        setExistedSRGasPump2Equipments: (state, action) => {
            state.existedSRGasPump2Equipments = action.payload;
        },
        setExistedSRGasPump3Equipments: (state, action) => {
            state.existedSRGasPump3Equipments = action.payload;
        },
        setShowHideGasSeparatorEquipment: (state, action) => {
            state.showGasSeparatorsEquipment = action.payload;
        },
        setShowHideGasHandlerEquipment: (state, action) => {
            state.showGasHandlerEquipments = action.payload;
        },
        setShowHideSRGasPump1Equipment: (state, action) => {
            state.showSRGasPump1Equipments = action.payload;
        },
        setShowHideSRGasPump2Equipment: (state, action) => {
            state.showSRGasPump2Equipments = action.payload;
        },
        setShowHideSRGasPump3Equipment: (state, action) => {
            state.showSRGasPump3Equipments = action.payload;
        },

        setGasOutputSP1Data: (state, action) => {
            if (state.GasCalculationOutPut) {
                state.GasCalculationOutPut = {
                    ...state?.GasCalculationOutPut,
                    SP1_Bg: action?.payload?.SP1_Bg ?? state.GasCalculationOutPut.SP1_Bg,
                    SP1_Bo: action?.payload?.SP1_Bo ?? state.GasCalculationOutPut.SP1_Bo,
                    SP1_FinalComposite_SpGr: action?.payload?.SP1_FinalComposite_SpGr ?? state.GasCalculationOutPut.SP1_FinalComposite_SpGr,
                    SP1_FinalFLOP: action?.payload?.SP1_FinalFLOP ?? state.GasCalculationOutPut.SP1_FinalFLOP,
                    SP1_FinalTemperature: action?.payload?.SP1_FinalTemperature ?? state.GasCalculationOutPut.SP1_FinalTemperature,
                    SP1_FreeGas: action?.payload?.SP1_FreeGas ?? state.GasCalculationOutPut.SP1_FreeGas,
                    SP1_GasCompressibility: action?.payload?.SP1_GasCompressibility ?? state.GasCalculationOutPut.SP1_GasCompressibility,
                    SP1_GLR: action?.payload?.SP1_GLR ?? state.GasCalculationOutPut.SP1_GLR,
                    SP1_GOR: action?.payload?.SP1_GOR ?? state.GasCalculationOutPut.SP1_GOR,
                    SP1_GVF: action?.payload?.SP1_GVF ?? state.GasCalculationOutPut.SP1_GVF,
                    SP1_LiquidPhaseDensity: action?.payload?.SP1_LiquidPhaseDensity ?? state.GasCalculationOutPut.SP1_LiquidPhaseDensity,
                    SP1_Pressure: action?.payload?.SP1_Pressure ?? state.GasCalculationOutPut.SP1_Pressure,
                    SP1_Rs: action?.payload?.SP1_Rs ?? state.GasCalculationOutPut.SP1_Rs,
                    SP1_TotalFluidVolume: action?.payload?.SP1_TotalFluidVolume ?? state.GasCalculationOutPut.SP1_TotalFluidVolume,
                    SP1_VolumeOfGas: action?.payload?.SP1_VolumeOfGas ?? state.GasCalculationOutPut.SP1_VolumeOfGas,
                    SP1_VolumeOfOil: action?.payload?.SP1_VolumeOfOil ?? state.GasCalculationOutPut.SP1_VolumeOfOil,
                    SP1_VolumeOfWater: action?.payload?.SP1_VolumeOfWater ?? state.GasCalculationOutPut.SP1_VolumeOfWater,
                    SP1_LiquidPhaseSpGr: action?.payload?.SP1_LiquidPhaseSpGr ?? state.GasCalculationOutPut.SP1_LiquidPhaseSpGr
                }
            }
        },
        setGasOutputSP2Data: (state, action) => {
            if (state.GasCalculationOutPut) {
                state.GasCalculationOutPut = {
                    ...state?.GasCalculationOutPut,
                    SP2_Bg: action?.payload?.SP2_Bg ?? state.GasCalculationOutPut.SP2_Bg,
                    SP2_Bo: action?.payload?.SP2_Bo ?? state.GasCalculationOutPut.SP2_Bo,
                    SP2_FinalComposite_SpGr: action?.payload?.SP2_FinalComposite_SpGr ?? state.GasCalculationOutPut.SP2_FinalComposite_SpGr,
                    SP2_FinalFLOP: action?.payload?.SP2_FinalFLOP ?? state.GasCalculationOutPut.SP2_FinalFLOP,
                    SP2_FinalTemperature: action?.payload?.SP2_FinalTemperature ?? state.GasCalculationOutPut.SP2_FinalTemperature,
                    SP2_FreeGas: action?.payload?.SP2_FreeGas ?? state.GasCalculationOutPut.SP2_FreeGas,
                    SP2_GasCompressibility: action?.payload?.SP2_GasCompressibility ?? state.GasCalculationOutPut.SP2_GasCompressibility,
                    SP2_GLR: action?.payload?.SP2_GLR ?? state.GasCalculationOutPut.SP2_GLR,
                    SP2_GOR: action?.payload?.SP2_GOR ?? state.GasCalculationOutPut.SP2_GOR,
                    SP2_GVF: action?.payload?.SP2_GVF ?? state.GasCalculationOutPut.SP2_GVF,
                    SP2_LiquidPhaseDensity: action?.payload?.SP2_LiquidPhaseDensity ?? state.GasCalculationOutPut.SP2_LiquidPhaseDensity,
                    SP2_Pressure: action?.payload?.SP2_Pressure ?? state.GasCalculationOutPut.SP2_Pressure,
                    SP2_Rs: action?.payload?.SP2_Rs ?? state.GasCalculationOutPut.SP2_Rs,
                    SP2_TotalFluidVolume: action?.payload?.SP2_TotalFluidVolume ?? state.GasCalculationOutPut.SP2_TotalFluidVolume,
                    SP2_VolumeOfGas: action?.payload?.SP2_VolumeOfGas ?? state.GasCalculationOutPut.SP2_VolumeOfGas,
                    SP2_VolumeOfOil: action?.payload?.SP2_VolumeOfOil ?? state.GasCalculationOutPut.SP2_VolumeOfOil,
                    SP2_VolumeOfWater: action?.payload?.SP2_VolumeOfWater ?? state.GasCalculationOutPut.SP2_VolumeOfWater,
                    SP2_LiquidPhaseSpGr: action?.payload?.SP2_LiquidPhaseSpGr ?? state.GasCalculationOutPut.SP2_LiquidPhaseSpGr
                }
            }
        },
        setGasOutputSP3Data: (state, action) => {
            if (state.GasCalculationOutPut) {
                state.GasCalculationOutPut = {
                    ...state?.GasCalculationOutPut,
                    SP3_Bg: action?.payload?.SP3_Bg ?? state.GasCalculationOutPut.SP3_Bg,
                    SP3_Bo: action?.payload?.SP3_Bo ?? state.GasCalculationOutPut.SP3_Bo,
                    SP3_FinalComposite_SpGr: action?.payload?.SP3_FinalComposite_SpGr ?? state.GasCalculationOutPut.SP3_FinalComposite_SpGr,
                    SP3_FinalFLOP: action?.payload?.SP3_FinalFLOP ?? state.GasCalculationOutPut.SP3_FinalFLOP,
                    SP3_FinalTemperature: action?.payload?.SP3_FinalTemperature ?? state.GasCalculationOutPut.SP3_FinalTemperature,
                    SP3_FreeGas: action?.payload?.SP3_FreeGas ?? state.GasCalculationOutPut.SP3_FreeGas,
                    SP3_GasCompressibility: action?.payload?.SP3_GasCompressibility ?? state.GasCalculationOutPut.SP3_GasCompressibility,
                    SP3_GLR: action?.payload?.SP3_GLR ?? state.GasCalculationOutPut.SP3_GLR,
                    SP3_GOR: action?.payload?.SP3_GOR ?? state.GasCalculationOutPut.SP3_GOR,
                    SP3_GVF: action?.payload?.SP3_GVF ?? state.GasCalculationOutPut.SP3_GVF,
                    SP3_LiquidPhaseDensity: action?.payload?.SP3_LiquidPhaseDensity ?? state.GasCalculationOutPut.SP3_LiquidPhaseDensity,
                    SP3_Pressure: action?.payload?.SP3_Pressure ?? state.GasCalculationOutPut.SP3_Pressure,
                    SP3_Rs: action?.payload?.SP3_Rs ?? state.GasCalculationOutPut.SP3_Rs,
                    SP3_TotalFluidVolume: action?.payload?.SP3_TotalFluidVolume ?? state.GasCalculationOutPut.SP3_TotalFluidVolume,
                    SP3_VolumeOfGas: action?.payload?.SP3_VolumeOfGas ?? state.GasCalculationOutPut.SP3_VolumeOfGas,
                    SP3_VolumeOfOil: action?.payload?.SP3_VolumeOfOil ?? state.GasCalculationOutPut.SP3_VolumeOfOil,
                    SP3_VolumeOfWater: action?.payload?.SP3_VolumeOfWater ?? state.GasCalculationOutPut.SP3_VolumeOfWater,
                    SP3_LiquidPhaseSpGr: action?.payload?.SP3_LiquidPhaseSpGr ?? state.GasCalculationOutPut.SP3_LiquidPhaseSpGr
                }
            }
        },
        setGasOutputSP4Data: (state, action) => {
            if (state.GasCalculationOutPut) {
                state.GasCalculationOutPut = {
                    ...state?.GasCalculationOutPut,
                    SP4_Bg: action?.payload?.SP4_Bg ?? state.GasCalculationOutPut.SP4_Bg,
                    SP4_Bo: action?.payload?.SP4_Bo ?? state.GasCalculationOutPut.SP4_Bo,
                    SP4_FinalComposite_SpGr: action?.payload?.SP4_FinalComposite_SpGr ?? state.GasCalculationOutPut.SP4_FinalComposite_SpGr,
                    SP4_FinalFLOP: action?.payload?.SP4_FinalFLOP ?? state.GasCalculationOutPut.SP4_FinalFLOP,
                    SP4_FinalTemperature: action?.payload?.SP4_FinalTemperature ?? state.GasCalculationOutPut.SP4_FinalTemperature,
                    SP4_FreeGas: action?.payload?.SP4_FreeGas ?? state.GasCalculationOutPut.SP4_FreeGas,
                    SP4_GasCompressibility: action?.payload?.SP4_GasCompressibility ?? state.GasCalculationOutPut.SP4_GasCompressibility,
                    SP4_GLR: action?.payload?.SP4_GLR ?? state.GasCalculationOutPut.SP4_GLR,
                    SP4_GOR: action?.payload?.SP4_GOR ?? state.GasCalculationOutPut.SP4_GOR,
                    SP4_GVF: action?.payload?.SP4_GVF ?? state.GasCalculationOutPut.SP4_GVF,
                    SP4_LiquidPhaseDensity: action?.payload?.SP4_LiquidPhaseDensity ?? state.GasCalculationOutPut.SP4_LiquidPhaseDensity,
                    SP4_Pressure: action?.payload?.SP4_Pressure ?? state.GasCalculationOutPut.SP4_Pressure,
                    SP4_Rs: action?.payload?.SP4_Rs ?? state.GasCalculationOutPut.SP4_Rs,
                    SP4_TotalFluidVolume: action?.payload?.SP4_TotalFluidVolume ?? state.GasCalculationOutPut.SP4_TotalFluidVolume,
                    SP4_VolumeOfGas: action?.payload?.SP4_VolumeOfGas ?? state.GasCalculationOutPut.SP4_VolumeOfGas,
                    SP4_VolumeOfOil: action?.payload?.SP4_VolumeOfOil ?? state.GasCalculationOutPut.SP4_VolumeOfOil,
                    SP4_VolumeOfWater: action?.payload?.SP4_VolumeOfWater ?? state.GasCalculationOutPut.SP4_VolumeOfWater,
                    SP4_LiquidPhaseSpGr: action?.payload?.SP4_LiquidPhaseSpGr ?? state.GasCalculationOutPut.SP4_LiquidPhaseSpGr
                }
            }
        },
        setGasOutputGKXData: (state, action) => {
            if (state.GasCalculationOutPut) {
                state.GasCalculationOutPut = {
                    ...state?.GasCalculationOutPut,
                    GKX_Bg: action?.payload?.GKX_Bg ?? state.GasCalculationOutPut.GKX_Bg,
                    GKX_Bo: action?.payload?.GKX_Bo ?? state.GasCalculationOutPut.GKX_Bo,
                    GKX_FinalComposite_SpGr: action?.payload?.GKX_FinalComposite_SpGr ?? state.GasCalculationOutPut.GKX_FinalComposite_SpGr,
                    GKX_FinalFLOP: action?.payload?.GKX_FinalFLOP ?? state.GasCalculationOutPut.GKX_FinalFLOP,
                    GKX_FinalTemperature: action?.payload?.GKX_FinalTemperature ?? state.GasCalculationOutPut.GKX_FinalTemperature,
                    GKX_FreeGas: action?.payload?.GKX_FreeGas ?? state.GasCalculationOutPut.GKX_FreeGas,
                    GKX_GasCompressibility: action?.payload?.GKX_GasCompressibility ?? state.GasCalculationOutPut.GKX_GasCompressibility,
                    GKX_GLR: action?.payload?.GKX_GLR ?? state.GasCalculationOutPut.GKX_GLR,
                    GKX_GOR: action?.payload?.GKX_GOR ?? state.GasCalculationOutPut.GKX_GOR,
                    GKX_GVF: action?.payload?.GKX_GVF ?? state.GasCalculationOutPut.GKX_GVF,
                    GKX_LiquidPhaseDensity: action?.payload?.GKX_LiquidPhaseDensity ?? state.GasCalculationOutPut.GKX_LiquidPhaseDensity,
                    GKX_NaturalSeperatorEff: action?.payload?.GKX_NaturalSeperatorEff ?? state.GasCalculationOutPut.GKX_NaturalSeperatorEff,
                    GKX_Pressure: action?.payload?.GKX_Pressure ?? state.GasCalculationOutPut.GKX_Pressure,
                    GKX_Rs: action?.payload?.GKX_Rs ?? state.GasCalculationOutPut.GKX_Rs,
                    GKX_TotalFluidVolume: action?.payload?.GKX_TotalFluidVolume ?? state.GasCalculationOutPut.GKX_TotalFluidVolume,
                    GKX_VolumeOfGas: action?.payload?.GKX_VolumeOfGas ?? state.GasCalculationOutPut.GKX_VolumeOfGas,
                    GKX_VolumeOfOil: action?.payload?.GKX_VolumeOfOil ?? state.GasCalculationOutPut.GKX_VolumeOfOil,
                    GKX_VolumeOfWater: action?.payload?.GKX_VolumeOfWater ?? state.GasCalculationOutPut.GKX_VolumeOfWater,
                    GKX_HPRating: action?.payload?.GKX_HPRating ?? state.GasCalculationOutPut.GKX_HPRating,
                    GKX_FlowCorrection: action?.payload?.GKX_FlowCorrection,
                    GKX_HeadCorrection: action?.payload?.GKX_HeadCorrection,
                    GKX_PowerCorrection: action?.payload?.GKX_PowerCorrection,
                    GKX_LiquidPhaseSpGr: action?.payload?.GKX_LiquidPhaseSpGr ?? state.GasCalculationOutPut.GKX_LiquidPhaseSpGr
                };
            }
        },
        setGasOutputCP1Data: (state, action) => {
            if (state.GasCalculationOutPut) {
                state.GasCalculationOutPut = {
                    ...state.GasCalculationOutPut,
                    CP1_Bg: action?.payload?.CP1_Bg ?? state?.GasCalculationOutPut?.CP1_Bg,
                    CP1_Bo: action?.payload?.CP1_Bo ?? state?.GasCalculationOutPut?.CP1_Bo,
                    CP1_FinalComposite_SpGr: action?.payload?.CP1_FinalComposite_SpGr ?? state?.GasCalculationOutPut?.CP1_FinalComposite_SpGr,
                    CP1_FinalFLOP: action?.payload?.CP1_FinalFLOP ?? state?.GasCalculationOutPut?.CP1_FinalFLOP,
                    CP1_FinalTemperature: action?.payload?.CP1_FinalTemperature ?? state?.GasCalculationOutPut?.CP1_FinalTemperature,
                    CP1_FreeGas: action?.payload?.CP1_FreeGas ?? state?.GasCalculationOutPut?.CP1_FreeGas,
                    CP1_GasCompressibility: action?.payload?.CP1_GasCompressibility ?? state?.GasCalculationOutPut?.CP1_GasCompressibility,
                    CP1_GLR: action?.payload?.CP1_GLR ?? state?.GasCalculationOutPut?.CP1_GLR,
                    CP1_GOR: action?.payload?.CP1_GOR ?? state?.GasCalculationOutPut?.CP1_GOR,
                    CP1_GVF: action?.payload?.CP1_GVF ?? state?.GasCalculationOutPut?.CP1_GVF,
                    CP1_LiquidPhaseDensity: action?.payload?.CP1_LiquidPhaseDensity ?? state?.GasCalculationOutPut?.CP1_LiquidPhaseDensity,
                    CP1_NaturalSeperatorEff: action?.payload?.CP1_NaturalSeperatorEff ?? state?.GasCalculationOutPut?.CP1_NaturalSeperatorEff,
                    CP1_Pressure: action?.payload?.CP1_Pressure ?? state?.GasCalculationOutPut?.CP1_Pressure,
                    CP1_Rs: action?.payload?.CP1_Rs ?? state?.GasCalculationOutPut?.CP1_Rs,
                    CP1_SeperatorEff: action?.payload?.CP1_SeperatorEff ?? state?.GasCalculationOutPut?.CP1_SeperatorEff,
                    CP1_TotalFluidVolume: action?.payload?.CP1_TotalFluidVolume ?? state?.GasCalculationOutPut?.CP1_TotalFluidVolume,
                    CP1_VolumeOfGas: action?.payload?.CP1_VolumeOfGas ?? state?.GasCalculationOutPut?.CP1_VolumeOfGas,
                    CP1_VolumeOfOil: action?.payload?.CP1_VolumeOfOil ?? state?.GasCalculationOutPut?.CP1_VolumeOfOil,
                    CP1_VolumeOfWater: action?.payload?.CP1_VolumeOfWater ?? state?.GasCalculationOutPut?.CP1_VolumeOfWater,
                    CP1_HPRating: action?.payload?.CP1_HPRating ?? state?.GasCalculationOutPut?.CP1_HPRating,
                    CP1_FlowCorrection: action?.payload?.CP1_FlowCorrection,
                    CP1_HeadCorrection: action?.payload?.CP1_HeadCorrection,
                    CP1_PowerCorrection: action?.payload?.CP1_PowerCorrection,
                    CP1_LiquidPhaseSpGr: action?.payload?.CP1_LiquidPhaseSpGr ?? state?.GasCalculationOutPut?.CP1_LiquidPhaseSpGr
                }
            }
        },
        setGasOutputCP2Data: (state, action) => {
            if (state.GasCalculationOutPut) {
                state.GasCalculationOutPut = {
                    ...state.GasCalculationOutPut,
                    CP2_Bg: action?.payload?.CP2_Bg ?? state?.GasCalculationOutPut?.CP2_Bg,
                    CP2_Bo: action?.payload?.CP2_Bo ?? state?.GasCalculationOutPut?.CP2_Bo,
                    CP2_FinalComposite_SpGr: action?.payload?.CP2_FinalComposite_SpGr ?? state?.GasCalculationOutPut?.CP2_FinalComposite_SpGr,
                    CP2_FinalFLOP: action?.payload?.CP2_FinalFLOP ?? state?.GasCalculationOutPut?.CP2_FinalFLOP,
                    CP2_FinalTemperature: action?.payload?.CP2_FinalTemperature ?? state?.GasCalculationOutPut?.CP2_FinalTemperature,
                    CP2_FreeGas: action?.payload?.CP2_FreeGas ?? state?.GasCalculationOutPut?.CP2_FreeGas,
                    CP2_GasCompressibility: action?.payload?.CP2_GasCompressibility ?? state?.GasCalculationOutPut?.CP2_GasCompressibility,
                    CP2_GLR: action?.payload?.CP2_GLR ?? state?.GasCalculationOutPut?.CP2_GLR,
                    CP2_GOR: action?.payload?.CP2_GOR ?? state?.GasCalculationOutPut?.CP2_GOR,
                    CP2_GVF: action?.payload?.CP2_GVF ?? state?.GasCalculationOutPut?.CP2_GVF,
                    CP2_LiquidPhaseDensity: action?.payload?.CP2_LiquidPhaseDensity ?? state?.GasCalculationOutPut?.CP2_LiquidPhaseDensity,
                    CP2_NaturalSeperatorEff: action?.payload?.CP2_NaturalSeperatorEff ?? state?.GasCalculationOutPut?.CP2_NaturalSeperatorEff,
                    CP2_Pressure: action?.payload?.CP2_Pressure ?? state?.GasCalculationOutPut?.CP2_Pressure,
                    CP2_Rs: action?.payload?.CP2_Rs ?? state?.GasCalculationOutPut?.CP2_Rs,
                    CP2_SeperatorEff: action?.payload?.CP2_SeperatorEff ?? state?.GasCalculationOutPut?.CP2_SeperatorEff,
                    CP2_TotalFluidVolume: action?.payload?.CP2_TotalFluidVolume ?? state?.GasCalculationOutPut?.CP2_TotalFluidVolume,
                    CP2_VolumeOfGas: action?.payload?.CP2_VolumeOfGas ?? state?.GasCalculationOutPut?.CP2_VolumeOfGas,
                    CP2_VolumeOfOil: action?.payload?.CP2_VolumeOfOil ?? state?.GasCalculationOutPut?.CP2_VolumeOfOil,
                    CP2_VolumeOfWater: action?.payload?.CP2_VolumeOfWater ?? state?.GasCalculationOutPut?.CP2_VolumeOfWater,
                    CP2_HPRating: action?.payload?.CP2_HPRating ?? state?.GasCalculationOutPut?.CP2_HPRating,
                    CP2_FlowCorrection: action?.payload?.CP2_FlowCorrection,
                    CP2_HeadCorrection: action?.payload?.CP2_HeadCorrection,
                    CP2_PowerCorrection: action?.payload?.CP2_PowerCorrection,
                    CP2_LiquidPhaseSpGr: action?.payload?.CP2_LiquidPhaseSpGr ?? state?.GasCalculationOutPut?.CP2_LiquidPhaseSpGr
                }
            }
        },
        setGasOutputCP3Data: (state, action) => {
            if (state.GasCalculationOutPut) {
                state.GasCalculationOutPut = {
                    ...state.GasCalculationOutPut,
                    CP3_Bg: action?.payload?.CP3_Bg ?? state?.GasCalculationOutPut?.CP3_Bg,
                    CP3_Bo: action?.payload?.CP3_Bo ?? state?.GasCalculationOutPut?.CP3_Bo,
                    CP3_FinalComposite_SpGr: action?.payload?.CP3_FinalComposite_SpGr ?? state?.GasCalculationOutPut?.CP3_FinalComposite_SpGr,
                    CP3_FinalFLOP: action?.payload?.CP3_FinalFLOP ?? state?.GasCalculationOutPut?.CP3_FinalFLOP,
                    CP3_FinalTemperature: action?.payload?.CP3_FinalTemperature ?? state?.GasCalculationOutPut?.CP3_FinalTemperature,
                    CP3_FreeGas: action?.payload?.CP3_FreeGas ?? state?.GasCalculationOutPut?.CP3_FreeGas,
                    CP3_GasCompressibility: action?.payload?.CP3_GasCompressibility ?? state?.GasCalculationOutPut?.CP3_GasCompressibility,
                    CP3_GLR: action?.payload?.CP3_GLR ?? state?.GasCalculationOutPut?.CP3_GLR,
                    CP3_GOR: action?.payload?.CP3_GOR ?? state?.GasCalculationOutPut?.CP3_GOR,
                    CP3_GVF: action?.payload?.CP3_GVF ?? state?.GasCalculationOutPut?.CP3_GVF,
                    CP3_LiquidPhaseDensity: action?.payload?.CP3_LiquidPhaseDensity ?? state?.GasCalculationOutPut?.CP3_LiquidPhaseDensity,
                    CP3_NaturalSeperatorEff: action?.payload?.CP3_NaturalSeperatorEff ?? state?.GasCalculationOutPut?.CP3_NaturalSeperatorEff,
                    CP3_Pressure: action?.payload?.CP3_Pressure ?? state?.GasCalculationOutPut?.CP3_Pressure,
                    CP3_Rs: action?.payload?.CP3_Rs ?? state?.GasCalculationOutPut?.CP3_Rs,
                    CP3_SeperatorEff: action?.payload?.CP3_SeperatorEff ?? state?.GasCalculationOutPut?.CP3_SeperatorEff,
                    CP3_TotalFluidVolume: action?.payload?.CP3_TotalFluidVolume ?? state?.GasCalculationOutPut?.CP3_TotalFluidVolume,
                    CP3_VolumeOfGas: action?.payload?.CP3_VolumeOfGas ?? state?.GasCalculationOutPut?.CP3_VolumeOfGas,
                    CP3_VolumeOfOil: action?.payload?.CP3_VolumeOfOil ?? state?.GasCalculationOutPut?.CP3_VolumeOfOil,
                    CP3_VolumeOfWater: action?.payload?.CP3_VolumeOfWater ?? state?.GasCalculationOutPut?.CP3_VolumeOfWater,
                    CP3_HPRating: action?.payload?.CP3_HPRating ?? state?.GasCalculationOutPut?.CP3_HPRating,
                    CP3_FlowCorrection: action?.payload?.CP3_FlowCorrection,
                    CP3_HeadCorrection: action?.payload?.CP3_HeadCorrection,
                    CP3_PowerCorrection: action?.payload?.CP3_PowerCorrection,
                    CP3_LiquidPhaseSpGr: action?.payload?.CP3_LiquidPhaseSpGr ?? state?.GasCalculationOutPut?.CP3_LiquidPhaseSpGr
                }
            }
        },
        setGasHandlerEquipmentsDropdown: (state, action) => {
            if (state?.BasicGasCalculation) {
                state.BasicGasCalculation = {
                    ...state.BasicGasCalculation,
                    EquipmentListForGasHandlers: action?.payload?.length > 0 ? action?.payload.map((equipment: any) => ({
                        ...equipment,
                        label: equipment?.gas_Handling,
                        value: equipment?.id,
                        diameter: equipment?.od?.toString(),
                    })) : [],
                }
            }
        },
        setGasGKXList: (state, action: PayloadAction<any>) => {
            if (state?.BasicGasCalculation) {
                const payload = action.payload;
                const isSelectedGkxExist = state.BasicGasCalculation?.chargePumpGKX && payload.some(
                    (item: any) => item?.value === state.BasicGasCalculation?.chargePumpGKX);
                state.BasicGasCalculation = {
                    ...state.BasicGasCalculation,
                    chargePumpGKXList: payload,
                    chargePumpGKX: (isSelectedGkxExist || payload?.length === 1) ? state.BasicGasCalculation.chargePumpGKX : ''
                };
            }
        },
        setGasGKXCalculationData: (state, action) => {
            state.GKXCalculationData = action?.payload;
        },
        setGasChargePump1Data: (state, action: PayloadAction<any>) => {
            state.gasChargePump1Data = action.payload;
        },
        setGasChargePump2Data: (state, action) => {
            state.gasChargePump2Data = action.payload;
        },
        setGasChargePump3Data: (state, action: PayloadAction<ChargePumpData>) => {
            state.gasChargePump3Data = action.payload;
        },
        setChargePump1EquipmentsDropdown: (state, action) => {
            if (state?.BasicGasCalculation) {
                state.BasicGasCalculation = {
                    ...state.BasicGasCalculation,
                    EquipmentListForChargePumpOne: action?.payload?.length > 0 ? action?.payload?.map((equipment: any) => ({
                        ...equipment,
                        value: equipment?.id,
                        label: equipment?.pN_Description,
                        text: equipment.pN_Description,
                        pn: equipment?.partNumber,
                        diameter: equipment?.od?.toString(),
                    })) : [],
                }
            }
        },
        setChargePump2EquipmentsDropdown: (state, action) => {
            if (state?.BasicGasCalculation) {
                state.BasicGasCalculation = {
                    ...state.BasicGasCalculation,
                    EquipmentListForChargePumpTwo: action?.payload?.length > 0 ? action?.payload?.map((equipment: any) => ({
                        ...equipment,
                        value: equipment?.id,
                        label: equipment?.pN_Description,
                        pn: equipment?.partNumber,
                        diameter: equipment?.od?.toString(),
                    })) : [],
                }
            }
        },
        setChargePump3EquipmentsDropdown: (state, action) => {
            if (state?.BasicGasCalculation) {
                state.BasicGasCalculation = {
                    ...state.BasicGasCalculation,
                    EquipmentListForChargePumpThree: action?.payload?.length > 0 ? action?.payload?.map((equipment: any) => ({
                        ...equipment,
                        value: equipment?.id,
                        label: equipment?.pN_Description,
                        pn: equipment?.partNumber,
                        diameter: equipment?.od?.toString(),
                    })) : [],
                }
            }
        },


        setCP1Power: (state, action) => {
        if (state.GasCalculationOutPut) {
            state.GasCalculationOutPut = {
                ...state.GasCalculationOutPut,
                CP1_HPRating: action?.payload
            }
        }
        },
         setCP2Power: (state, action) => {
        if (state.GasCalculationOutPut) {
            state.GasCalculationOutPut = {
                ...state.GasCalculationOutPut,
                CP2_HPRating: action?.payload
            }
        }
        },
         setCP3Power: (state, action) => {
        if (state.GasCalculationOutPut) {
            state.GasCalculationOutPut = {
                ...state.GasCalculationOutPut,
                CP3_HPRating: action?.payload
            }
        }
        },

        setGasIntakeNaturalEff: (state, action) => {
            if (state.GasCalculationOutPut) {
                state.GasCalculationOutPut = {
                    ...state.GasCalculationOutPut,
                    Intake_NaturalSeperatorEff: action?.payload
                }
            }
        },
        setGasIntakeNaturalEff2: (state, action) => {
            if (state.GasCalculationOutPut) {
                state.GasCalculationOutPut = {
                    ...state.GasCalculationOutPut,
                    Intake_NaturalSeperatorEff: action?.payload
                }
            }
        },
        setGasGKXCorrectionFactors: (state, action: PayloadAction<any>) => {
            if (state.GasCalculationOutPut) {
                const { flowCorrection, headCorrection, powerCorrection } = action.payload;
                state.GasCalculationOutPut = {
                    ...state.GasCalculationOutPut,
                    GKX_FlowCorrection: flowCorrection ?? state?.GasCalculationOutPut?.GKX_FlowCorrection,
                    GKX_HeadCorrection: headCorrection ?? state?.GasCalculationOutPut?.GKX_HeadCorrection,
                    GKX_PowerCorrection: powerCorrection ?? state?.GasCalculationOutPut?.GKX_PowerCorrection,
                }
            }
        },
        setChargePump1CorrectionFactors: (state, action: PayloadAction<any>) => {
            if (state.GasCalculationOutPut) {
                const { flowCorrection, headCorrection, powerCorrection } = action?.payload;
                state.GasCalculationOutPut = {
                    ...state.GasCalculationOutPut,
                    CP1_FlowCorrection: flowCorrection ?? state?.GasCalculationOutPut?.GKX_FlowCorrection,
                    CP1_HeadCorrection: headCorrection ?? state?.GasCalculationOutPut?.GKX_HeadCorrection,
                    CP1_PowerCorrection: powerCorrection ?? state?.GasCalculationOutPut?.GKX_PowerCorrection,
                }
            }
        },
        setChargePump2CorrectionFactors: (state, action: PayloadAction<any>) => {
            if (state.GasCalculationOutPut) {
                const { flowCorrection, headCorrection, powerCorrection } = action?.payload;
                state.GasCalculationOutPut = {
                    ...state.GasCalculationOutPut,
                    CP2_FlowCorrection: flowCorrection ?? state?.GasCalculationOutPut?.CP2_FlowCorrection,
                    CP2_HeadCorrection: headCorrection ?? state?.GasCalculationOutPut?.CP2_HeadCorrection,
                    CP2_PowerCorrection: powerCorrection ?? state?.GasCalculationOutPut?.CP2_PowerCorrection,
                }
            }
        },
        setChargePump3CorrectionFactors: (state, action: PayloadAction<any>) => {
            if (state.GasCalculationOutPut) {
                const { flowCorrection, headCorrection, powerCorrection } = action?.payload;
                state.GasCalculationOutPut = {
                    ...state.GasCalculationOutPut,
                    CP3_FlowCorrection: flowCorrection ?? state?.GasCalculationOutPut?.CP3_FlowCorrection,
                    CP3_HeadCorrection: headCorrection ?? state?.GasCalculationOutPut?.CP3_HeadCorrection,
                    CP3_PowerCorrection: powerCorrection ?? state?.GasCalculationOutPut?.CP3_PowerCorrection,
                }
            }
        },
        setChargePump1Equipments: (state, action) => {
            if (state?.BasicGasCalculation) {
                state.BasicGasCalculation = {
                    ...state.BasicGasCalculation,
                    EquipmentListForChargePumpOne: action.payload
                }
            }
        },
        setChargePump2Equipments: (state, action) => {
            if (state?.BasicGasCalculation) {
                state.BasicGasCalculation = {
                    ...state.BasicGasCalculation,
                    EquipmentListForChargePumpTwo: action.payload
                }
            }
        },
        setChargePump3Equipments: (state, action) => {
            if (state?.BasicGasCalculation) {
                state.BasicGasCalculation = {
                    ...state.BasicGasCalculation,
                    EquipmentListForChargePumpThree: action.payload
                }
            }
        },
        setCP1GraphData: (state, action) => {
            state.CP1GraphCurveData = action.payload;
        },
        setCP2GraphData: (state, action) => {
            state.CP2GraphCurveData = action.payload;
        },
        setCP3GraphData: (state, action) => {
            state.CP3GraphCurveData = action.payload;
        },
        setChargePumpName: (state, action) => {
            state.chargePumpName = action?.payload;
        },
        setChargePump2Name: (state, action) => {
            state.chargePump2Name = action?.payload;
        },
        setChargePump3Name: (state, action) => {
            state.chargePump3Name = action?.payload;
        },
        setTFVSepWarning: (state, action) => {
            if (state.GasCalculationOutPut) {
                state.GasCalculationOutPut = {
                    ...state.GasCalculationOutPut,
                    IsTFV_SepWarningVisible: action?.payload
                }
            }
        },
        setFinalCalculationData: (state, action) => {
            state.GasFinalCalculatedData = action?.payload;
        },
        setGasCalculationComplete: (state, action) => {
            state.GasCalculationComplete = action?.payload;
        },
        setGKXPumpCapcityGraphData: (state, action) => {
            state.gkxPumpCapacityCurveData = action?.payload;
        },
        setChargePump1CapcityGraphData: (state, action) => {
            state.chargePumpCapacityCurveData = action?.payload;
        },
        setChargePump2CapcityGraphData: (state, action) => {
            state.chargePump2CapacityCurveData = action?.payload;
        },
        setChargePump3CapcityGraphData: (state, action) => {
            state.chargePump3CapacityCurveData = action?.payload;
        },
        setGasNotification: (state, action) => {
            state.Notification = action.payload;
        }
    },
});

export const {
    resetGascalculations,
    setGasCalculation,
    setGasCalculation2,
    setGasCalculationOutput,
    setFinalCalculationData,
    setGasCalculationComplete,
    setGasDesign,
    setGasSeparatorList,
    setSelectedGasSeparator,
    setSepName,
    setNoOfSeparators,
    setGasAnchorOD,
    setGasTubingOD,
    setGasAnchorID,
    setGasTubingODList,
    setAnchorODList,
    setChargePumpList,
    setChargePumpSelected,
    setChargePumpList2,
    setChargePump2Selected,
    setChargePumpList3,
    setChargePump3Selected,
    setUseGasHandler,
    setGasHandler,
    setTypeOfShaft,
    setGasPowerRating,
    setUseChargePump,
    setTypeOfShaft1,
    setTypeOfShaft2,
    setTypeOfShaft3,
    setDesiredNoOfStages1,
    setDesiredNoOfStages2,
    setDesiredNoOfStages3,
    setGasIntakeOutput,
    setGasAnchorOutput,
    setGasSeparatorsEquipmentDropdown,
    setShowHideGasSeparatorEquipment,
    setShowHideGasHandlerEquipment,
    setShowHideSRGasPump1Equipment,
    setShowHideSRGasPump2Equipment,
    setShowHideSRGasPump3Equipment,
    setGasOutputSP1Data,
    setGasOutputSP2Data,
    setGasOutputSP3Data,
    setGasOutputSP4Data,
    setGasOutputGKXData,
    setGasGKXList,
    setExistedGasSepEquipments,
    setExistedGasHandlerEquipments,
    setExistedSRGasPump1Equipments,
    setExistedSRGasPump2Equipments,
    setExistedSRGasPump3Equipments,
    setGasGKXCalculationData,
    setGasChargePump1Data,
    setGasChargePump2Data,
    setGasChargePump3Data,
    setGasHandlerEquipmentsDropdown,
    setChargePump1EquipmentsDropdown,
    setChargePump2EquipmentsDropdown,
    setChargePump3EquipmentsDropdown,
    setGasIntakeNaturalEff,
    setGasIntakeNaturalEff2,
    setGasGKXCorrectionFactors,
    setChargePump1CorrectionFactors,
    setChargePump2CorrectionFactors,
    setChargePump3CorrectionFactors,
    setChargePump1Equipments,
    setChargePump2Equipments,
    setChargePump3Equipments,
    setGasOutputCP1Data,
    setGasOutputCP2Data,
    setGasOutputCP3Data,
    setCP1GraphData,
    setCP2GraphData,
    setCP3GraphData,
    setChargePumpName,
    setChargePump2Name,
    setChargePump3Name,
    setTFVSepWarning,
    setGKXPumpCapcityGraphData,
    setChargePump1CapcityGraphData,
    setChargePump2CapcityGraphData,
    setChargePump3CapcityGraphData,
    setGasNotification,
    setCP1Power,
    setCP2Power,
    setCP3Power
} = gasCalculationSlice.actions;

export default gasCalculationSlice.reducer;
